import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { searchApi } from "@/api/endpoints";
import type { SearchResults } from "@/types";

/** Staff-only command-palette-style search across orgs/users/buildings. */
export default function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResults | null>(null);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!q.trim()) {
      setResults(null);
      return;
    }
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      searchApi.query(q).then(setResults).catch(() => setResults(null));
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [q]);

  const goto = (path: string) => {
    setOpen(false);
    setQ("");
    setResults(null);
    navigate(path);
  };

  const total =
    (results?.organisations.length ?? 0) +
    (results?.users.length ?? 0) +
    (results?.buildings.length ?? 0);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
        <input
          type="text"
          placeholder="Search orgs, users, buildings"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="w-full text-sm bg-slate-50 border border-slate-200 rounded-md pl-7 pr-7 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:bg-white"
        />
        {q && (
          <button
            onClick={() => {
              setQ("");
              setResults(null);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && q.trim() && (
        <div
          className="absolute left-0 right-0 top-full mt-2 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto"
          onMouseLeave={() => setOpen(false)}
        >
          {results === null && <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>}
          {results && total === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">No matches for "{q}"</div>
          )}
          {results && results.organisations.length > 0 && (
            <div className="border-b border-slate-100 last:border-b-0">
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase text-slate-400 tracking-wide">
                Organisations
              </div>
              {results.organisations.map((o) => (
                <button
                  key={o.id}
                  onClick={() => goto(`/staff/organisations/${o.id}`)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between"
                >
                  <span className="truncate">{o.name}</span>
                  {!o.is_active && (
                    <span className="text-[10px] text-slate-400 ml-2">inactive</span>
                  )}
                </button>
              ))}
            </div>
          )}
          {results && results.users.length > 0 && (
            <div className="border-b border-slate-100 last:border-b-0">
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase text-slate-400 tracking-wide">
                Users
              </div>
              {results.users.map((u) => (
                <button
                  key={u.id}
                  onClick={() => u.organisation_id ? goto(`/staff/organisations/${u.organisation_id}/users`) : null}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                >
                  <div className="truncate">{u.name ?? u.email}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </button>
              ))}
            </div>
          )}
          {results && results.buildings.length > 0 && (
            <div>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase text-slate-400 tracking-wide">
                Buildings
              </div>
              {results.buildings.map((b) => (
                <button
                  key={b.id}
                  onClick={() => goto(`/staff/organisations/${b.organisation_id}/buildings/${b.id}`)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                >
                  <div className="truncate">{b.name}</div>
                  <div className="text-xs text-slate-500 truncate">{b.city}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
